/**
 * The visible crowd.
 *
 * These are VISIBLE NPCs in the brief's sense: they have positions, they are
 * rendered, they walk between real places, and they never touch money. The
 * households that actually shape the economy are BACKGROUND ECONOMIC AGENTS and
 * live in `WorldSimulation` with no position at all. Keeping the two apart is
 * what lets the crowd stay cheap while the economy scales independently.
 *
 * They are built from the same `Humanoid` as the player, so the neighbourhood
 * is populated by people rather than by a different, cheaper species.
 */
import * as THREE from 'three';
import {
  buildHumanoid, HumanoidAnimator,
  SKIN_TONES, HAIR_COLOURS, SHIRT_COLOURS, TROUSER_COLOURS,
} from './Humanoid.js';
import type { WorldSimulation } from '../../sim/systems/WorldSimulation.js';

export interface CrowdRig {
  group: THREE.Group;
  update(
    dt: number, world: WorldSimulation, hour: number, viewX: number, viewZ: number,
  ): void;
  /** 0..1 fraction of the crowd to draw, for the graphics-quality setting. */
  setBudget(fraction: number): void;
  count: number;
}

/** Beyond this many metres a figure is a few pixels; stop drawing it. */
const CULL_DISTANCE = 78;

interface CrowdMember {
  root: THREE.Group;
  animator: HumanoidAnimator;
  lastX: number;
  lastZ: number;
  facing: number;
  /** Personal speed multiplier, so the crowd is not one metronome. */
  pace: number;
  /** Hours this person is out. Night streets should not look like midday. */
  activeFrom: number;
  activeTo: number;
  visible: boolean;
  fade: number;
}

export function createCrowd(world: WorldSimulation): CrowdRig {
  const group = new THREE.Group();
  const npcs = world.getVisibleNpcs();
  const members: CrowdMember[] = [];

  // Deterministic styling, so the same seed always yields the same neighbours.
  let seed = 0x5f3a19;
  const rand = () => {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    return seed / 0x7fffffff;
  };
  const pick = <T>(list: readonly T[]): T => list[Math.floor(rand() * list.length)] as T;

  for (const npc of npcs) {
    const parts = buildHumanoid({
      skin: pick(SKIN_TONES),
      hair: pick(HAIR_COLOURS),
      shirt: pick(SHIRT_COLOURS),
      trousers: pick(TROUSER_COLOURS),
      shoes: pick([0x2b2b30, 0x4a3a2a, 0x1a1a1f, 0x6a5a4a]),
      height: npc.height,
      hat: rand() < 0.18 ? pick([0x8a3f3f, 0x3a5f8a, 0x4a4f5a, 0x7a5a3a]) : null,
      bulk: 0.9 + rand() * 0.28,
    });
    const root = new THREE.Group();
    root.add(parts.root);
    root.position.set(npc.x, 0, npc.z);
    group.add(root);

    // Rough daily rhythm: most people are out in the day, a few late, a few
    // early. This is what makes 22:00 feel different from 12:00.
    const roll = rand();
    const activeFrom = roll < 0.15 ? 5 : roll < 0.85 ? 7 : 10;
    const activeTo = roll < 0.15 ? 19 : roll < 0.85 ? 21.5 : 24;

    members.push({
      root,
      animator: new HumanoidAnimator(parts),
      lastX: npc.x,
      lastZ: npc.z,
      facing: 0,
      pace: 0.82 + rand() * 0.45,
      activeFrom,
      activeTo,
      visible: true,
      fade: 1,
    });
  }

  function update(
    dt: number, sim: WorldSimulation, hour: number, viewX: number, viewZ: number,
  ): void {
    sim.tickVisible(dt, hour);
    const list = sim.getVisibleNpcs();

    for (let i = 0; i < members.length && i < list.length; i++) {
      const npc = list[i]!;
      const m = members[i]!;

      // Off-shift people are scaled away rather than popped out, so the street
      // thins and fills instead of blinking.
      const onShift = hour >= m.activeFrom && hour < m.activeTo;
      // Beyond the cull radius they are a handful of pixels and a dozen draw
      // calls each; the crowd is the largest remaining draw-call source.
      // The budget thins the crowd deterministically by index rather than at
      // random, so lowering quality does not make people flicker in and out.
      const withinBudget = i < members.length * budget;
      const far = !withinBudget
        || Math.hypot(npc.x - viewX, npc.z - viewZ) > CULL_DISTANCE;
      m.fade += ((onShift && !far ? 1 : 0) - m.fade) * Math.min(1, dt * (far ? 6 : 1.5));
      m.root.visible = m.fade > 0.02;
      m.root.scale.setScalar(Math.max(0.02, m.fade));
      if (!m.root.visible) {
        m.lastX = npc.x;
        m.lastZ = npc.z;
        continue;
      }

      const dx = npc.x - m.lastX;
      const dz = npc.z - m.lastZ;
      const travelled = Math.hypot(dx, dz);
      const speed = dt > 0 ? travelled / dt : 0;

      m.root.position.set(npc.x, 0, npc.z);
      m.lastX = npc.x;
      m.lastZ = npc.z;

      if (travelled > 0.0005) {
        const target = Math.atan2(dx, dz);
        let diff = ((target - m.facing + Math.PI) % (Math.PI * 2)) - Math.PI;
        if (diff < -Math.PI) diff += Math.PI * 2;
        m.facing += diff * Math.min(1, dt * 7);
        m.root.rotation.y = m.facing;
      }

      // A person standing outside a shop should idle, not moonwalk.
      m.animator.update(dt, speed * m.pace, false, 0, 'locomotion');
    }
  }

  let budget = 1;

  return {
    group, update, count: members.length,
    setBudget(fraction: number): void {
      budget = Math.max(0.1, Math.min(1, fraction));
    },
  };
}
